import type { MenuProps } from "antd";

import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Layout, Typography, Menu, Drawer, Button, Grid, Row, Col } from "antd";
import { MenuOutlined, HomeOutlined, UserOutlined, DashboardOutlined } from "@ant-design/icons";

import { sendGAEvent } from "../../utils";
import { useAuth } from "../../hooks";
import { TerraformandoIcon } from '../TerraformandoIcon';
import Cookies from "js-cookie";

const { Header } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

// Determina qual menu está ativo
const getSelectedKey = (pathname: string) => {
  if (pathname.startsWith("/perfil")) return "profile";
  if (pathname.startsWith("/login")) return "login";
  if (pathname.startsWith("/dashboard")) return "dashboard";
  if (pathname === "/") return "home";

  // Adicione outras regras conforme novos menus
  return "";
};

const AppHeader = () => {
  const { isAuthenticated } = useAuth();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const screens = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems: MenuProps['items'] = [
    {
      key: "home",
      icon: <HomeOutlined />,
      label: (
        <span
          onClick={() => sendGAEvent("menu_click", { menu: "home" })}
          data-ga-event="menu_click"
          data-ga-label="home"
        >
          Home
        </span>
      ),
      onClick: () => navigate("/"),
    },
    {
      key: "profile",
      icon: <UserOutlined />,
      label: (
        <span
          onClick={() => sendGAEvent("menu_click", { menu: "perfil" })}
          data-ga-event="menu_click"
          data-ga-label="perfil"
        >
          Perfil
        </span>
      ),
      onClick: () => navigate("/perfil"),
    },
    isAuthenticated
      ? {
        key: "dashboard",
        icon: <DashboardOutlined />,
        label: (
          <span
            onClick={() => sendGAEvent("menu_click", { menu: "dashboard" })}
            data-ga-event="menu_click"
            data-ga-label="dashboard"
          >
            Dashboard
          </span>
        ),
        onClick: () => navigate("/dashboard"),
      }
      : null,
    !isAuthenticated
      ? {
        key: "login",
        icon: <UserOutlined />,
        label: (
          <span
            onClick={() => navigate("/login")}
            data-ga-event="menu_click"
            data-ga-label="login"
          >
            Login
          </span>
        ),
        onClick: () => navigate("/login"),
      }
      : null,
    isAuthenticated ? {
      key: "logout",
      icon: <UserOutlined />,
      label: (
        <span
          onClick={() => {
            sendGAEvent("menu_click", { menu: "logout" });
            Cookies.remove("token");
            navigate("/login");
          }}
          data-ga-event="menu_click"
          data-ga-label="logout"
        >
          Logout
        </span>
      ),
      onClick: () => {
        Cookies.remove("token");
        navigate("/login");
      },
    } : null,
  ].filter((item): item is Exclude<typeof item, null> => Boolean(item));

  const selectedKey = getSelectedKey(location.pathname);

  return (
    <Header
      style={{
        backgroundColor: "#fff",
        borderBottom: "1px solid #eaeaea",
        padding: "5px 15px",
        position: screens.md ? "fixed" : "static",
        top: screens.md ? 0 : "auto",
        left: 0,
        width: screens.md ? "100%" : "auto",
        zIndex: 2,
        height: 60,
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto", height: "100%" }}>
        <Row
          style={{ height: "100%" }}
          align="middle"
          justify="space-between"
          wrap={false}
        >
          {/* Logo */}
          <Col xs={20} md={8} flex="none">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer"
              }}
              onClick={() => {
                if (location.pathname !== "/") {
                  navigate("/");
                }
              }}
            >
              <div style={{
                marginRight: 12,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                height: '100%'
              }}>
                <TerraformandoIcon size={screens.md ? 40 : 32} />
              </div>
              <div style={{
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                lineHeight: "1.2",
                maxWidth: "clamp(180px, 25vw, 300px)",
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%'
              }}>
                <Title
                  level={4}
                  style={{
                    margin: 0,
                    fontSize: 'clamp(16px, 4vw, 18px)',
                    background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontWeight: 700
                  }}
                >
                  Terraformando
                </Title>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 'clamp(10px, 2.5vw, 12px)',
                  }}
                >
                  Arquitetura Frontend de Alta Performance
                </Text>
              </div>
            </div>
          </Col>

          {/* Menu Desktop - SÓ APARECE NO DESKTOP */}
          {screens.md &&
            <Col
              xs={0} sm={0} md={16}
              style={{
                display: "flex",
                justifyContent: "flex-end"
              }}
            >
              <Menu
                mode="horizontal"
                selectedKeys={[selectedKey]}
                items={menuItems}
                style={{
                  borderBottom: "none",
                  background: "transparent",
                  fontWeight: 500,
                  fontSize: 16,
                  minWidth: "auto"
                }}
              />
            </Col>
          }

          {/* Botão Menu Mobile - SÓ APARECE NO MOBILE */}
          <Col
            xs={4} sm={4} md={0}
            style={{
              display: screens.md ? "none" : "flex",
              justifyContent: "flex-end"
            }}
          >
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setDrawerVisible(true)}
              style={{
                fontSize: '20px',
                height: '45px',
                width: '45px'
              }}
              aria-label="Abrir menu de navegação"
            />
          </Col>
        </Row>
      </div>
      {/* Drawer Mobile */}
      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="vertical"
          selectedKeys={[selectedKey]}
          items={menuItems}
          style={{ border: "none" }}
          onClick={() => setDrawerVisible(false)}
        />
      </Drawer>
    </Header>
  );
};

export default AppHeader;